export const IMAGE_UPSCALE_PATH = '/image-upscale';
export const IMAGE_UPSCALE_DATE_PUBLISHED = '2026-07-29';
export const IMAGE_UPSCALE_LAST_UPDATED = '2026-07-29';
export const IMAGE_UPSCALE_LAST_UPDATED_LABEL = 'July 29, 2026';

export const IMAGE_UPSCALE_SEO_TITLE =
  '4K Image Upscaler: Preview 2x/4x Output Online';
export const IMAGE_UPSCALE_SEO_DESCRIPTION =
  'Upload JPG, PNG, or WebP up to 10MB. Choose 2x or 4x, preview output dimensions, and check 4K UHD readiness before using 20 site credits.';
export const IMAGE_UPSCALE_KEYWORDS = [
  '4k image upscaler',
  'upscale image to 4k',
  'image 4k upscaler',
  '4k upscale image',
  'ai image upscaler',
  'image upscaler 4k',
  'upscale photo to 4k',
  'increase image resolution',
  '2x image upscaler',
  '4x image upscaler',
  'online image upscaler',
].join(', ');

export const IMAGE_UPSCALE_QUICK_ANSWER =
  'AI image upscaling enlarges pixel dimensions and reconstructs edges and texture. This 4K image upscaler supports 2x and 4x, preserves aspect ratio, costs 20 site credits per image, and marks an output 4K UHD-ready only when it reaches at least 3840 x 2160 pixels.';

export const IMAGE_UPSCALE_STEPS = [
  {
    name: 'Upload the best source image',
    text: 'Choose a JPG, PNG, or WebP up to 10MB. Start from the least-compressed original available because enlargement cannot recover every detail removed by blur, heavy JPEG compression, or an earlier resize.',
  },
  {
    name: 'Choose 2x or 4x',
    text: 'Use 2x for a moderate enlargement and 4x for the largest output. The 4K image upscaler multiplies both dimensions and labels the estimate as 4K UHD-ready only when the long edge reaches 3840 pixels and the short edge reaches 2160 pixels.',
  },
  {
    name: 'Run the AI image upscale',
    text: 'Review the estimated dimensions and the 20-site-credit cost, then start the task. The pending job and local upload are preserved if login or navigation interrupts the workflow.',
  },
  {
    name: 'Compare and download',
    text: 'Switch the 4K image upscaler between Original and Upscaled at the same preview size. Inspect faces, text, product labels, thin lines, repeated patterns, and high-contrast edges before downloading the saved result.',
  },
] as const;

export const IMAGE_UPSCALE_FEATURES = [
  {
    title: 'Know whether the image can reach 4K',
    body: 'The workbench reads the source width and height, applies the selected multiplier, and shows the estimated output before credits are used. This distinguishes a true 4K-sized output from a result that is simply larger.',
  },
  {
    title: 'Choose a practical 2x or 4x enlargement',
    body: 'The 4K image upscaler doubles width and height at 2x for four times the pixel area. At 4x, it multiplies each dimension by four for sixteen times the pixel area, which is useful when a small source must fill a larger export.',
  },
  {
    title: 'Keep the original aspect ratio',
    body: 'The 4K image upscaler does not crop or stretch the source. Portrait, landscape, and square images keep their original proportions while their pixel dimensions increase.',
  },
  {
    title: 'Review the result in the same workflow',
    body: 'Completed upscales appear in the recent strip and History. When the source is recoverable, the preview exposes Original and Upscaled tabs so the enlargement can be judged instead of accepted on resolution alone.',
  },
] as const;

export const IMAGE_UPSCALE_USE_CASES = [
  {
    title: 'Prepare a small AI image for a larger canvas',
    input:
      'A generated concept, character, scene, or product image that looks good but is too small for the next layout.',
    action:
      'Send the saved image into the 4K image upscaler, choose the smallest multiplier that reaches the delivery size, and review fragile details.',
    result:
      'A larger working file ready for editing, presentation, social publishing, or another creative pass.',
  },
  {
    title: 'Enlarge product and marketing images',
    input:
      'A compressed marketplace image, campaign asset, or product photograph that must occupy more pixels in a page or presentation.',
    action:
      'Use the 4K image upscaler on the cleanest source, then compare labels, straight edges, logos, material texture, and background transitions.',
    result:
      'A larger asset for layout work, with visible defects identified before it reaches a live campaign.',
  },
  {
    title: 'Upscale a photo toward a 4K display target',
    input:
      'A 960 x 540, 1280 x 720, 1920 x 1080, or similarly sized photo intended for a larger screen.',
    action:
      'Choose 2x or 4x in the 4K image upscaler according to the displayed output calculation. Use 4K UHD-ready as a size check, not a promise of new factual detail.',
    result:
      'A larger photo whose dimensions fit the target and whose faces, texture, and edge quality have been reviewed.',
  },
] as const;

export const IMAGE_UPSCALE_LIMITS = [
  {
    title: '4x does not always mean 4K',
    body: 'A 4K image upscaler multiplier describes how much each dimension grows. An 800 x 800 source becomes 3200 x 3200 at 4x, so it does not reach the 3840 x 2160 UHD reference even though its total pixel count is much larger.',
  },
  {
    title: 'AI reconstruction is an estimate',
    body: 'The model can create plausible texture and cleaner edges, but it cannot recover exact information that was never present. Critical identity, legal text, scientific detail, and product specifications need human verification.',
  },
  {
    title: 'Tiny text and logos can change',
    body: 'Small typography, watermarks, interface labels, signatures, and geometric brand marks are sensitive to reconstruction. Compare these regions at 100% zoom and replace them from vector or source files when precision matters.',
  },
  {
    title: 'The source still determines the ceiling',
    body: 'Strong motion blur, missed focus, clipped highlights, blocked shadows, and repeated compression artifacts may remain visible or become more obvious after enlargement. Use the cleanest original before applying the 4K upscale image step.',
  },
  {
    title: 'Input and output limits apply',
    body: 'The current tool accepts JPG, PNG, and WebP inputs up to 10MB. The provider caps the longest output edge at 20,000 pixels, preserves the source aspect ratio, and returns one upscaled image per task.',
  },
  {
    title: 'Resolution is not print quality by itself',
    body: 'Print sharpness also depends on physical size, viewing distance, paper, ink, and effective pixels per inch. Check the print provider specification after upscaling rather than treating the 4K label as a universal print standard.',
  },
] as const;

export const IMAGE_UPSCALE_FAQS = [
  {
    question: 'What is a 4K image upscaler?',
    answer:
      'A 4K image upscaler is an image enlargement tool that increases pixel dimensions and uses AI reconstruction to refine edges and texture. This page marks an output 4K UHD-ready only when the long edge reaches 3840 pixels and the short edge reaches 2160 pixels. The completed file dimensions remain the final authority.',
  },
  {
    question: 'How do I upscale an image to 4K?',
    answer:
      'Upload a JPG, PNG, or WebP, choose 2x or 4x, and read the estimated output in the workbench. A 1920 x 1080 source reaches 3840 x 2160 at 2x; a 960 x 540 source reaches the same size at 4x. Start the task, compare Original and Upscaled, then download the result.',
  },
  {
    question: 'Does 4x upscale an image to 4K every time?',
    answer:
      'A 4x upscale does not always produce a 4K UHD image. Four times the dimensions is not the same as a fixed 4K target. Multiply the source width and height by four and check whether the output reaches at least 3840 x 2160 pixels. The workbench performs this calculation automatically.',
  },
  {
    question: 'Should I choose 2x or 4x image upscale?',
    answer:
      'Choose the smallest 4K image upscaler factor that reaches the required delivery dimensions. 2x is a moderate enlargement and is often enough for a 1920 x 1080 source. Use 4x for smaller sources or larger layouts, then inspect invented texture and fragile details more carefully.',
  },
  {
    question: 'Can an AI image upscaler fix a blurry photo?',
    answer:
      'An AI image upscaler can improve perceived edge clarity and generate plausible detail at a larger size, but it cannot restore exact focus or factual detail that the camera did not capture. Severe blur, faces, text, logos, and product details should be checked manually after processing.',
  },
] as const;

export const IMAGE_UPSCALE_SCHEMA_FEATURES = [
  '2x and 4x AI image upscaling',
  '4K output dimension estimate',
  'JPG, PNG, and WebP input',
  'Original and upscaled comparison',
  'Persistent workbench draft and history',
  'Saved image download workflow',
] as const;

export function getImageUpscaleSeoData() {
  return {
    path: IMAGE_UPSCALE_PATH,
    seoTitle: IMAGE_UPSCALE_SEO_TITLE,
    seoDescription: IMAGE_UPSCALE_SEO_DESCRIPTION,
    keywords: IMAGE_UPSCALE_KEYWORDS,
    datePublished: IMAGE_UPSCALE_DATE_PUBLISHED,
    lastUpdated: IMAGE_UPSCALE_LAST_UPDATED,
    lastUpdatedLabel: IMAGE_UPSCALE_LAST_UPDATED_LABEL,
    quickAnswer: IMAGE_UPSCALE_QUICK_ANSWER,
    steps: IMAGE_UPSCALE_STEPS,
    features: IMAGE_UPSCALE_FEATURES,
    useCases: IMAGE_UPSCALE_USE_CASES,
    limits: IMAGE_UPSCALE_LIMITS,
    faqs: IMAGE_UPSCALE_FAQS,
    schemaFeatures: IMAGE_UPSCALE_SCHEMA_FEATURES,
  };
}
